import { Flex } from "@mantine/core";
import { type ReactNode, useCallback } from "react";

import { useChatWorkspaceData } from "@/components/chat/hooks/useChatWorkspaceData";
import { GuidedFlowsPanel } from "@/components/workspace/GuidedFlowsPanel";
import { CustomerPanelWrapper } from "@/customer-panel/CustomerPanelWrapper";
import { Panels } from "@/panels";
import { ProductSaleFlowPanel } from "@/sales-tools/product-sale-flow/ProductSaleFlowPanel";
import { useLogger } from "@/sdk";
import { selectChatRightBottomTab, useChatStore } from "@/sdk/agent/chat";
import { PlatformTimeline } from "@/timeline";

import ChatControlsBar from "./ChatControls/ChatControlsBar";
import { ChatPanelTimelineView } from "./ChatPanelTimelineView";
import { ChatQueue } from "./ChatQueue";

/** provides a consistent layout for panel slots */
function SlotPanel({ children, ...props }: { children: ReactNode }) {
    return (
        <Flex h="100%" pos="absolute" style={{ containerType: "inline-size" }} inset={0} {...props}>
            {children}
        </Flex>
    );
}

/**
 * Legacy chat workspace (the `<Panels>` wrapper layout), rendered when `isChatWorkspaceRedesignV2Enabled`
 * is off. Shares its chat/session data orchestration with the redesigned `ChatWorkspaceContentRedesignedV2`
 * via {@link useChatWorkspaceData} (single source of truth — no drift); this component owns only the
 * legacy `<Panels>` layout and its right-bottom Assist/Flows tab selection.
 */
export function ChatManager() {
    const logger = useLogger({ module: "ChatManager", supportTeam: "messaging" });
    const {
        chatTasks,
        activeChatId,
        activeChatTask,
        onChatSelected,
        isSelectedChat,
        canShowChatDetails,
        shouldShowQueue,
        isSalesEnabled,
        isChatExpertAssistEnabled,
        areGuidesAvailable,
        assistTabProps: { guide, guideToolboxCommand, isGuideOpen, onGuideClose },
        selectedProduct,
        productSaleFlowPanelTitle,
        handleProductSaleClose,
    } = useChatWorkspaceData({ logger, isRedesignV2: false });

    const savedRightBottomTab = useChatStore(selectChatRightBottomTab(activeChatId));

    const shouldShowSalesPanel = canShowChatDetails && isSalesEnabled;

    // Right bottom panel configuration
    const shouldShowAssistTab = canShowChatDetails && isChatExpertAssistEnabled;
    const shouldShowFlowsTab = canShowChatDetails && selectedProduct;

    // Resolve the per-chat tab selection. If the user has not made a choice on this chat,
    // default to Flows when a product exists, otherwise Assist. If their saved choice is
    // Flows but no product is available, fall back to Assist (Flows tab is closed).
    const rightBottomSelectedTab = !shouldShowFlowsTab ? "a" : (savedRightBottomTab ?? "b");

    const handleRightBottomTabChange = useCallback((tab: "a" | "b") => {
        const currentChatId = useChatStore.getState().activeChatId;
        if (currentChatId) {
            useChatStore.getState().setChatRightBottomTab(currentChatId, tab);
        }
    }, []);

    return (
        <Panels
            leftMainPanelDefaultSize={60}
            leftSidebarSlot={
                shouldShowQueue && (
                    <SlotPanel>
                        <ChatQueue chats={chatTasks} isSelectedChat={isSelectedChat} onChatSelected={onChatSelected} />
                    </SlotPanel>
                )
            }
            leftSidebarConfig={{
                isCollapsed: !shouldShowQueue,
            }}
            topbarSlot={
                canShowChatDetails &&
                activeChatTask && (
                    <SlotPanel>
                        <ChatControlsBar chatTask={activeChatTask} />
                    </SlotPanel>
                )
            }
            topbarConfig={{
                isCollapsed: !canShowChatDetails,
                minHeight: "70px",
                maxHeight: "70px",
            }}
            rightSidebarSlot={
                shouldShowSalesPanel && (
                    <SlotPanel>
                        <CustomerPanelWrapper />
                    </SlotPanel>
                )
            }
            rightSidebarConfig={{
                isCollapsed: !shouldShowSalesPanel,
            }}
            leftTopASlot={<ChatPanelTimelineView activeChatTask={activeChatTask} />}
            leftTopConfig={{
                a: {
                    headerTitle: "Chat",
                    tabTitle: "Chat",
                    isMinimized: false,
                    isClosed: false,
                    showHeader: false,
                },
                b: {
                    headerTitle: "Tab B",
                    tabTitle: "Tab B",
                    isMinimized: false,
                    isClosed: true,
                    showHeader: false,
                },
            }}
            rightTopASlot={
                areGuidesAvailable && (
                    <SlotPanel>
                        <GuidedFlowsPanel guide={guide} />
                    </SlotPanel>
                )
            }
            rightTopConfig={
                areGuidesAvailable
                    ? {
                          selectedTab: "a",
                          a: {
                              headerTitle: "Guided Flows",
                              tabTitle: "Guided Flows",
                              isMinimized: false,
                              isClosed: !isGuideOpen,
                              showHeader: true,
                              onClose: onGuideClose,
                          },
                      }
                    : undefined
            }
            rightBottomASlot={
                shouldShowAssistTab && (
                    <SlotPanel>
                        <PlatformTimeline guide={guideToolboxCommand} />
                    </SlotPanel>
                )
            }
            rightBottomBSlot={shouldShowFlowsTab && <ProductSaleFlowPanel />}
            rightBottomConfig={{
                selectedTab: rightBottomSelectedTab,
                onTabChange: handleRightBottomTabChange,
                a: {
                    headerTitle: "Assist",
                    tabTitle: "Assist",
                    isMinimized: false,
                    isClosed: !shouldShowAssistTab,
                    showHeader: false,
                },
                b: {
                    headerTitle: productSaleFlowPanelTitle,
                    tabTitle: "Flows",
                    isMinimized: false,
                    isClosed: !shouldShowFlowsTab,
                    showHeader: true,
                    onClose: handleProductSaleClose,
                },
            }}
        />
    );
}
